// src/contexts/BranchContext.jsx

import React, { createContext, useContext, useState, useEffect } from 'react';

// Context 생성
const BranchContext = createContext();

// Provider 컴포넌트 생성
export function BranchProvider({ children }) {
    // 초기값: 로컬 스토리지에 저장된 값이 있으면 그거 쓰고, 없으면 기본값(null 또는 삼성점 등)
    const [currentBranch, setCurrentBranch] = useState(() => {
        // 기본 지점 값
        const defaultBranch = {
            brKey: '',
            brName: '',
            isProductAvailable: 'N',
            availableStKinds: ['E'], // 기본값으로 슬릭부스트 노출
            footerInfo: {
                companyName: '',
                ceoName: '',
                businessNumber: '',
                ecommerceNumber: '',
                address: '',
                csPhone: '',
                email: ''
            }
        };

        // [무한로딩(도넛 스피너) 버그 수정]
        // selectedBranch가 손상(부분 쓰기/오염)되면 JSON.parse가 throw하는데,
        // 이 초기화는 최상위 BranchProvider에서 실행되므로 앱 트리 전체가 마운트에 실패한다.
        // 프로덕션(웹뷰)에선 화면이 비어 네이티브 로딩 스피너가 무한히 도는 현상으로 나타난다.
        // 파싱 실패 시 손상 값을 제거하고 기본값으로 안전하게 복구한다.
        try {
            const saved = localStorage.getItem('selectedBranch');
            if (!saved) return defaultBranch;
            const parsed = JSON.parse(saved);
            // 객체 형태가 아니면(손상/구버전) 기본값 사용
            if (!parsed || typeof parsed !== 'object') {
                localStorage.removeItem('selectedBranch');
                return defaultBranch;
            }
            return parsed;
        } catch (e) {
            console.error('selectedBranch 파싱 실패 - 기본값으로 복구:', e);
            localStorage.removeItem('selectedBranch');
            return defaultBranch;
        }
    });

    // 지점이 변경될 때마다 로컬 스토리지도 업데이트
    useEffect(() => {
        if (currentBranch) {
            localStorage.setItem('selectedBranch', JSON.stringify(currentBranch));
        }
    }, [currentBranch]);

    // 지점 변경 함수
    // [무한로딩 버그 수정] 실질적으로 동일한 값이면 이전 참조를 유지한다.
    // Home의 loadDashboardData가 currentBranch에 의존하는데 내부에서 selectBranch로
    // currentBranch를 매번 새 객체로 교체하면, 인증완료 effect([..., loadDashboardData])가
    // 재실행 → loadDashboardData 재생성 → selectBranch → ... 무한 루프(+/home 재요청 폭주)에 빠진다.
    // (특히 서버 footerInfo.companyName이 빈 값일 때 isFooterEmpty가 계속 true로 남아 정착 실패)
    // 값이 같으면 상태를 바꾸지 않아 참조가 안정되고 루프가 종료된다.
    const selectBranch = (branch) => {
        setCurrentBranch((prev) => {
            if (prev && JSON.stringify(prev) === JSON.stringify(branch)) {
                return prev;
            }
            return branch;
        });
    };

    return (
        <BranchContext.Provider value={{ currentBranch, selectBranch }}>
            {children}
        </BranchContext.Provider>
    );
}

// 커스텀 훅 (각 페이지에서 편하게 쓰기 위함)
export function useBranch() {
    const context = useContext(BranchContext);
    if (!context) {
        throw new Error('useBranch must be used within a BranchProvider');
    }
    return context;
}